import { PencilIcon } from "@heroicons/react/outline";
import { useState } from "react";
import {
  CareerTrackRoleType,
  CompetencyAppliesTo,
  CompetencyCriteriaUniqueness,
  CompetencyNode,
  OrganizationCompetencyCriteriaFragment,
  OrganizationCompetencyFragment,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { classNames } from "@helpers/css";

import AppliesToTag from "../applies-to-tag";
import CompetencyFormModal from "../competency-form-modal";
import { getCompetencySharing } from "../competency-sharing";
import CompetencySharingChip from "../competency-sharing-chip";
import CriteriaEditor from "../criteria-editor";
import {
  addLevelColClassName,
  bodyRowLineClassName,
  getCompetencyScopeSection,
  roleColWidth,
  roleLevelIterator,
  stickyCellBottomLineClassName,
  stickyColWidth,
} from "../helpers";
import ScopeSectionIcon from "../scope-section-icon";
import StickyColumnShadow from "../sticky-column-shadow";
import { CompetencyCriteriaInputWithUUID } from "../types";
import { CareerTrackFormRole } from "./career-track-form";
import EmptyCriteriaCell from "./empty-criteria-cell";

const CareerTrackCompetencyRow = ({
  organizationId,
  careerTrackId,
  competency,
  levelCount,
  levels = roleLevelIterator(levelCount),
  readOnly,
  roles,
  onDeleteCriteria,
  onCriteriaChange,
  onCompetenciesUpdated,
  onCompetencyDeleted,
  onAddRole,
  sharedCriteria,
  onSharedCriteriaChange,
  onDeleteSharedCriteria,
  onBlurCriteria,
  flushSave,
}: {
  organizationId: number;
  // The track being viewed, so the sharing message can say "other tracks".
  careerTrackId?: number | null;
  competency: Pick<
    CompetencyNode,
    | "id"
    | "title"
    | "criteriaUniqueness"
    | "appliesTo"
    | "appliesToIcRoles"
    | "appliesToManagementRoles"
  > &
    Pick<
      OrganizationCompetencyFragment,
      "appliesToTeams" | "appliesToCareerTracks"
    > &
    OrganizationCompetencyCriteriaFragment;
  levelCount: number;
  // The level columns shown, when not all of them are.
  levels?: number[];
  // View mode: the criteria read, with nothing to change them.
  readOnly?: boolean;
  roles: CareerTrackFormRole[];
  onDeleteCriteria: (roleUuid: string, criteriaUuid: string) => void;
  onCriteriaChange: (
    roleUuid: string,
    level: number,
    items: ReadonlyArray<{
      uuid?: string;
      content?: string | null;
    }>,
  ) => void;
  onCompetenciesUpdated: () => void;
  // Deleting is not just another update: the form has to forget the
  // competency as well as refetch the list.
  onCompetencyDeleted: () => void;
  // Creates the track's role for a level that hasn't got one yet.
  onAddRole: (level: number) => void;
  // The competency's shared criteria for every level (same-across-roles
  // competencies), including unsaved edits made in this table.
  sharedCriteria: CompetencyCriteriaInputWithUUID[];
  // Edits to shared criteria apply to the competency itself (every track that
  // uses it) and are saved along with the career track.
  onSharedCriteriaChange: (criteria: CompetencyCriteriaInputWithUUID[]) => void;
  // The bin button on a shared criterion, by its key.
  onDeleteSharedCriteria: (criteriaUuid: string) => void;
  // Autosaving pages save when a criterion loses focus.
  onBlurCriteria?: () => void;
  // Saves what's outstanding; false if that failed.
  flushSave?: () => Promise<boolean>;
}) => {
  const label = useLabel();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const sharing = getCompetencySharing(competency, { label, careerTrackId });
  const deleteConfirmation = {
    title: `Delete ${label("competency")} criteria?`,
    message: `Are you sure you want to delete this criteria? ${sharing.deleteEffect}`,
  };
  return (
    <>
      {isEditModalOpen && (
        <CompetencyFormModal
          organizationId={organizationId}
          competencyId={competency.id}
          onClose={() => setIsEditModalOpen(false)}
          onCompetencySaved={onCompetenciesUpdated}
          onCompetencyDeleted={onCompetencyDeleted}
          showSharedWarning
        />
      )}
      <tr
        key={competency.id}
        className={classNames("h-52 align-top", bodyRowLineClassName)}
      >
        <td
          className={classNames(
            stickyColWidth,
            "group/competency p-4 border border-l-0 sticky left-0 bg-white z-10",
            stickyCellBottomLineClassName,
          )}
        >
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-2 font-medium mt-1">
              <ScopeSectionIcon
                scope={getCompetencyScopeSection(competency, careerTrackId)}
                className="mt-0.5"
              />
              <div className="min-w-0">
                {competency.title}
                {/* Who it reaches matters while editing; Previewing is for
                    reading what's expected. */}
                {!readOnly && (
                  <div>
                    <AppliesToTag
                      competency={competency}
                      careerTrackId={careerTrackId}
                    />
                  </div>
                )}
              </div>
            </div>
            {!readOnly && (
              <Button
                theme={buttonTheme.iconGray}
                icon={PencilIcon}
                className="opacity-0 transition-opacity group-hover/competency:opacity-100 focus:opacity-100"
                tooltip={`Edit ${label("competency")}`}
                aria-label="Career track: Edit competency"
                // The dialog opens on what's saved, so edits still in the
                // boxes are saved first.
                onClick={async () => {
                  if (flushSave && !(await flushSave())) {
                    errorNotificationVar({
                      title: "Your latest changes couldn't be saved.",
                    });
                    return;
                  }
                  setIsEditModalOpen(true);
                }}
                size={ButtonSize.medium}
              />
            )}
          </div>
          <StickyColumnShadow />
        </td>
        {levels.map((level) => {
          const role = roles.find((role) => role.level === level);
          const isShared =
            competency.criteriaUniqueness ===
            CompetencyCriteriaUniqueness.SameAcrossRoles;

          const matchesRoleType =
            !role ||
            competency.appliesTo === CompetencyAppliesTo.AllRoles ||
            (!competency.appliesToIcRoles &&
              !competency.appliesToManagementRoles) ||
            (competency.appliesToIcRoles &&
              role.roleType === CareerTrackRoleType.Ic) ||
            (competency.appliesToManagementRoles &&
              role.roleType === CareerTrackRoleType.Management);
          const criteria = isShared
            ? matchesRoleType
              ? sharedCriteria.filter((criteria) => criteria.level === level)
              : []
            : (role?.criteriaByCompetencyId[competency.id] ?? []);

          // Criteria written per role need a role. Shared ones belong to the
          // competency, so anything already written shows on a level this
          // track has no role on — but an empty cell there says the role is
          // missing rather than inviting criteria for a role that isn't.
          if (!role && (!isShared || criteria.length === 0)) {
            return (
              <EmptyCriteriaCell
                key={level}
                onAddRole={readOnly ? undefined : () => onAddRole(level)}
              />
            );
          }

          return (
            <td
              className={classNames(
                roleColWidth,
                "p-4 border group/cell",
                criteria.length === 0 && "align-middle bg-content-background",
              )}
              key={level}
            >
              <CriteriaEditor
                activeNote={<CompetencySharingChip sharing={sharing} />}
                onBlurCriteria={onBlurCriteria}
                deleteConfirmation={deleteConfirmation}
                revealAddOnHover
                placeholder={`Enter ${label("competency")} criteria`}
                emptyState={{
                  message: "No criteria at this level",
                  actionText: "Add Criteria",
                }}
                centerEmptyState
                criteria={criteria}
                matchesRoleType={matchesRoleType}
                // A mismatch means the competency is for one role type only,
                // and this role is the other.
                roleTypeMismatchMessage={
                  role?.roleType === CareerTrackRoleType.Management
                    ? `${competency.title} is only for individual contributor roles. This is a management role.`
                    : `${competency.title} is only for management roles. This is an individual contributor role.`
                }
                editable={matchesRoleType && !readOnly}
                onAddCriteria={() =>
                  isShared
                    ? onSharedCriteriaChange([
                        ...sharedCriteria,
                        { level, content: "", uuid: uuidv4() },
                      ])
                    : role &&
                      onCriteriaChange(role.uuid, level, [{ content: "" }])
                }
                onPasteMultipleCriteria={(items) => {
                  if (!isShared) {
                    if (role) {
                      onCriteriaChange(role.uuid, level, items);
                    }
                    return;
                  }
                  const updatedUuids = items.map(({ uuid }) => uuid);
                  onSharedCriteriaChange([
                    ...sharedCriteria.filter(
                      ({ uuid }) => !updatedUuids.includes(uuid),
                    ),
                    ...items.map((item) => ({
                      ...sharedCriteria.find(({ uuid }) => uuid === item.uuid),
                      level,
                      content: item.content,
                      uuid: item.uuid || uuidv4(),
                    })),
                  ]);
                }}
                onDeleteCriteria={(uuid) =>
                  isShared
                    ? onDeleteSharedCriteria(uuid)
                    : role && onDeleteCriteria(role.uuid, uuid)
                }
                onCriteriaChange={(uuid, content) =>
                  isShared
                    ? onSharedCriteriaChange(
                        sharedCriteria.map((criteria) =>
                          criteria.uuid === uuid
                            ? { ...criteria, content }
                            : criteria,
                        ),
                      )
                    : role &&
                      onCriteriaChange(role.uuid, level, [{ uuid, content }])
                }
              />
            </td>
          );
        })}
        <td className={addLevelColClassName} />
      </tr>
    </>
  );
};

export default CareerTrackCompetencyRow;
